import React, { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import { ASSETS } from "@/data/content";
import { useI18n } from "@/i18n";

export default function Bydgoszcz() {
  const { t } = useI18n();
  const BYDGOSZCZ = t.bydgoszcz;
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => {
      if (e.key === "Escape") setLightbox(null);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      window.__lenis?.start();
    };
  }, [lightbox]);

  const openImage = (src, alt) => setLightbox({ src, alt });

  const walkReverse = {};
  let imgCount = 0;
  BYDGOSZCZ.walks.forEach((w, i) => {
    if (w.image) {
      walkReverse[i] = imgCount % 2 === 1;
      imgCount += 1;
    }
  });

  return (
    <section
      id="bydgoszcz"
      data-testid="bydgoszcz-section"
      className="relative bg-canvas-surface py-14 md:py-20 overflow-hidden"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Intro */}
        <div className="grid grid-cols-12 gap-8 md:gap-16 items-start mb-14 md:mb-20">
          <div className="col-span-12 md:col-span-6">
            <Reveal>
              <p className="overline mb-6">{BYDGOSZCZ.overline}</p>
              <h2 className="serif text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight text-ink">
                {BYDGOSZCZ.headingA} <span className="italic text-brand-red/80">{BYDGOSZCZ.headingB}</span>
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 md:pt-2">
            <Reveal delay={0.15}>
              <p className="serif text-xl md:text-2xl font-light text-ink leading-snug mb-6 [text-wrap:pretty]">
                {BYDGOSZCZ.lead}
              </p>
              <p className="text-ink-muted text-base leading-relaxed [text-wrap:pretty]">
                {BYDGOSZCZ.body}
              </p>
            </Reveal>
          </div>
        </div>

        {/* Wide hero image — nabrzeże Brdy */}
        <Reveal>
          <button
            type="button"
            onClick={() =>
              openImage(ASSETS.byWaterfront, BYDGOSZCZ.heroAlt)
            }
            aria-label={`${t.common.zoom}: ${BYDGOSZCZ.heroAlt}`}
            data-testid="bydgoszcz-hero-image"
            className="group block w-full aspect-[16/9] overflow-hidden cursor-zoom-in mb-16 md:mb-24"
          >
            <img
              src={ASSETS.byWaterfront}
              alt={BYDGOSZCZ.heroAlt}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out"
              style={{ objectPosition: "center 40%" }}
            />
          </button>
        </Reveal>

        {/* Spacer po Bydgoszczy — nagłówek grupy */}
        <Reveal className="mb-12 md:mb-16 block">
          <div className="flex items-center gap-5">
            <span className="h-px w-12 bg-brand-red/60" />
            <h3 className="serif text-3xl md:text-4xl font-light text-ink leading-tight">
              {BYDGOSZCZ.walkGroup}
            </h3>
          </div>
        </Reveal>

        {/* Naprzemienne bloki tekst + zdjęcie (część miejsc bez zdjęcia) */}
        <div className="flex flex-col gap-16 md:gap-24 mb-20 md:mb-28">
          {BYDGOSZCZ.walks.map((w, i) => {
            if (!w.image) {
              return (
                <Reveal key={w.title}>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
                    <div>
                      <h4 className="serif text-2xl md:text-3xl font-light text-ink leading-tight">
                        {w.title}
                      </h4>
                      <p className="mt-4 text-ink-muted text-base leading-relaxed [text-wrap:pretty]">
                        {w.desc}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            }
            const reverse = walkReverse[i];
            return (
              <Stagger
                key={w.title}
                className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center"
              >
                <div className={reverse ? "lg:order-2" : ""}>
                  <StaggerItem>
                    <h4 className="serif text-2xl md:text-3xl lg:text-4xl font-light leading-tight text-ink">
                      {w.title}
                    </h4>
                    <p className="mt-6 text-ink-muted text-base leading-relaxed [text-wrap:pretty]">
                      {w.desc}
                    </p>
                  </StaggerItem>
                </div>
                <div className={reverse ? "lg:order-1" : ""}>
                  <StaggerItem>
                    <button
                      type="button"
                      onClick={() => openImage(ASSETS[w.image], w.alt)}
                      aria-label={`Powiększ zdjęcie: ${w.alt}`}
                      data-testid={`bydgoszcz-walk-image-${i}`}
                      className="group block w-full aspect-[4/3] overflow-hidden cursor-zoom-in"
                    >
                      <img
                        src={ASSETS[w.image]}
                        alt={w.alt}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700 ease-out"
                      />
                    </button>
                  </StaggerItem>
                </div>
              </Stagger>
            );
          })}
        </div>

        {/* Muzea i wyjątkowe miejsca */}
        <Reveal className="mb-12 md:mb-16 block">
          <div className="flex items-center gap-5">
            <span className="h-px w-12 bg-brand-red/60" />
            <h3 className="serif text-3xl md:text-4xl font-light text-ink leading-tight">
              {BYDGOSZCZ.museumsGroup}
            </h3>
          </div>
        </Reveal>

        <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-12 mb-16 md:mb-24">
          {BYDGOSZCZ.museums.map((m, i) => (
            <StaggerItem key={m.title}>
              <div className="flex items-baseline gap-3 mb-3">
                <span className="text-brand-red text-xs tracking-[0.3em]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="h-px flex-1 bg-ink/20" />
              </div>
              <h4 className="serif text-2xl md:text-[1.6rem] font-light text-ink leading-tight">
                {m.title}
              </h4>
              <p className="mt-3 text-ink-muted text-sm leading-relaxed [text-wrap:pretty]">
                {m.desc}
              </p>
            </StaggerItem>
          ))}
        </Stagger>

        {/* Wide image — zabytkowy tramwaj */}
        <Reveal>
          <button
            type="button"
            onClick={() =>
              openImage(ASSETS.byTram, BYDGOSZCZ.tramAlt)
            }
            aria-label={`${t.common.zoom}: ${BYDGOSZCZ.tramAlt}`}
            data-testid="bydgoszcz-tram-image"
            className="group block w-full aspect-[16/9] overflow-hidden cursor-zoom-in mb-16 md:mb-24"
          >
            <img
              src={ASSETS.byTram}
              alt={BYDGOSZCZ.tramAlt}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700 ease-out"
            />
          </button>
        </Reveal>

        {/* Bydgoszcz od strony rzeki + Dla aktywnych */}
        <Stagger className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 mb-20 md:mb-28">
          {BYDGOSZCZ.activities.map((a) => (
            <StaggerItem key={a.title}>
              <div className="border-t border-ink/15 pt-6">
                <h4 className="serif text-2xl md:text-[1.7rem] font-light text-ink leading-tight">
                  {a.title}
                </h4>
                <p className="mt-4 text-ink-muted text-sm md:text-base leading-relaxed [text-wrap:pretty]">
                  {a.desc}
                </p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>

        {/* Smaki Bydgoszczy — blok zamykający ze zdjęciem lodów */}
        <Stagger className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          <div>
            <StaggerItem>
              <p className="overline text-brand-red mb-4">{BYDGOSZCZ.tasteKicker}</p>
              <h4 className="serif text-2xl md:text-3xl lg:text-4xl font-light leading-tight text-ink">
                {BYDGOSZCZ.taste.title}
              </h4>
              <p className="mt-6 text-ink-muted text-base leading-relaxed [text-wrap:pretty]">
                {BYDGOSZCZ.taste.desc}
              </p>
            </StaggerItem>
          </div>
          <div>
            <StaggerItem>
              <button
                type="button"
                onClick={() => openImage(ASSETS.byIce, BYDGOSZCZ.taste.alt)}
                aria-label={`Powiększ zdjęcie: ${BYDGOSZCZ.taste.alt}`}
                data-testid="bydgoszcz-taste-image"
                className="group block w-full aspect-[4/3] overflow-hidden cursor-zoom-in"
              >
                <img
                  src={ASSETS.byIce}
                  alt={BYDGOSZCZ.taste.alt}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700 ease-out"
                />
              </button>
            </StaggerItem>
          </div>
        </Stagger>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="bydgoszcz-lightbox"
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setLightbox(null)}
          >
            <div className="absolute inset-0 bg-ink/85 backdrop-blur-sm" />
            <button
              type="button"
              onClick={() => setLightbox(null)}
              data-testid="bydgoszcz-lightbox-close"
              aria-label="Zamknij"
              className="absolute top-6 right-6 z-10 text-canvas/80 hover:text-canvas transition-colors"
            >
              <X size={28} strokeWidth={1.4} />
            </button>
            <motion.img
              src={lightbox.src}
              alt={lightbox.alt}
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.94 }}
              transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
              className="relative z-10 max-h-[90vh] max-w-[92vw] object-contain shadow-2xl"
            />
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
